// Diary service for user-specific tarot reading management
import api from './api';
import authService from './authService';

class DiaryService {
  // Save a tarot reading to user's diary
  async saveReading(readingData) {
    try {
      // Use the main API service which now supports user authentication
      return await api.saveReading(readingData);
    } catch (error) {
      console.error('Save reading error:', error);
      throw error;
    }
  }

  // Get all readings for the authenticated user
  async getUserReadings(page = 1, limit = 20, filters = {}) {
    try {
      if (!authService.isAuthenticated()) {
        // Fall back to session-based readings if not authenticated
        return { readings: await api.getReadings(), totalPages: 1 };
      }

      // Calculate offset from page
      const offset = (page - 1) * limit;
      
      // Get user readings using the new API method (now returns pagination data)
      const result = await api.getUserReadings(limit, offset);
      console.log('API Result:', result);
      
      // Handle both old and new API response formats
      let readings, pagination;
      if (result.readings && result.pagination) {
        // New format with pagination
        readings = result.readings;
        pagination = result.pagination;
      } else if (Array.isArray(result)) {
        // Old format - just an array of readings
        readings = result;
        pagination = {
          currentPage: page,
          totalPages: Math.ceil(readings.length / limit),
          totalReadings: readings.length,
          hasNextPage: false,
          hasPreviousPage: false
        };
      } else {
        console.error('Unexpected API response format:', result);
        readings = [];
        pagination = {
          currentPage: 1,
          totalPages: 1,
          totalReadings: 0,
          hasNextPage: false,
          hasPreviousPage: false
        };
      }
      
      // Apply client-side filtering if needed
      let filteredReadings = readings;
      if (filters.spreadType && filters.spreadType !== 'all') {
        console.log('Applying filter:', filters.spreadType);
        console.log('Total readings before filter:', readings.length);
        
        filteredReadings = readings.filter(reading => {
          // Check both the spread name and metadata.spreadType
          const spreadName = reading.spread?.toLowerCase() || '';
          const metadataSpreadType = reading.metadata?.spreadType?.toLowerCase() || '';
          const filterType = filters.spreadType.toLowerCase();
          
          console.log('Reading:', {
            spread: reading.spread,
            spreadName,
            metadataSpreadType,
            filterType,
            matchesSpreadName: spreadName.includes(filterType),
            matchesMetadata: metadataSpreadType === filterType
          });
          
          return spreadName.includes(filterType) || metadataSpreadType === filterType;
        });
        
        console.log('Filtered readings count:', filteredReadings.length);
      }
      
      return {
        readings: filteredReadings,
        totalPages: pagination?.totalPages || 1,
        currentPage: pagination?.currentPage || 1,
        total: pagination?.totalReadings || filteredReadings.length,
        hasNextPage: pagination?.hasNextPage || false,
        hasPreviousPage: pagination?.hasPreviousPage || false
      };
    } catch (error) {
      console.error('Get readings error:', error);
      throw error;
    }
  }

  // Get a specific reading by ID
  async getReading(readingId) {
    try {
      // Get all readings and find the specific one
      const isAuth = authService.isAuthenticated();
      const response = isAuth ? await api.getUserReadings() : await api.getReadings();
      
      // Handle both old format (array) and new paginated format (object with readings array)
      let readings;
      if (Array.isArray(response)) {
        readings = response;
      } else if (response && response.readings && Array.isArray(response.readings)) {
        readings = response.readings;
      } else {
        console.error('Unexpected response format in getReading:', response);
        throw new Error('Invalid response format from API');
      }
      
      const reading = readings.find(r => r.id === readingId || r._id === readingId);
      if (!reading) {
        throw new Error('Reading not found');
      }
      
      return reading;
    } catch (error) {
      console.error('Get reading error:', error);
      throw error;
    }
  }

  // Update a reading (for AI explanations and other updates)
  async updateReading(readingId, updateData) {
    try {
      if (!authService.isAuthenticated()) {
        throw new Error('Authentication required to update readings. Please log in.');
      }
      
      // Use the main API service instead of direct fetch
      return await api.updateReading(readingId, updateData);
    } catch (error) {
      console.error('Update reading error:', error);
      throw error;
    }
  }

  // Delete a reading
  async deleteReading(readingId) {
    try {
      return await api.deleteReading(readingId);
    } catch (error) {
      console.error('Delete reading error:', error);
      throw error;
    }
  }

  // Get reading statistics for the user
  async getReadingStats() {
    try {
      if (!authService.isAuthenticated()) {
        // Fall back to session stats if not authenticated
        return await api.getStats();
      }

      return await api.getUserStats();
    } catch (error) {
      console.error('Get stats error:', error);
      throw error;
    }
  }

  // Search readings by text content
  async searchReadings(query, filters = {}) {
    try {
      const isAuth = authService.isAuthenticated();
      const response = isAuth ? await api.getUserReadings() : await api.getReadings();
      
      // Handle both old format (array) and new paginated format (object with readings array)
      let readings;
      if (Array.isArray(response)) {
        readings = response;
      } else if (response && response.readings && Array.isArray(response.readings)) {
        readings = response.readings;
      } else {
        console.error('Unexpected response format in searchReadings:', response);
        readings = [];
      }
      
      // Client-side search since API doesn't support search yet
      const filtered = readings.filter(reading => {
        const searchFields = [
          reading.spread,
          reading.question,
          reading.notes,
          JSON.stringify(reading.cards)
        ].join(' ').toLowerCase();
        
        return searchFields.includes(query.toLowerCase());
      });

      return { readings: filtered };
    } catch (error) {
      console.error('Search readings error:', error);
      throw error;
    }
  }

  // Export user's diary data
  async exportDiary(format = 'json') {
    try {
      const isAuth = authService.isAuthenticated();
      const response = isAuth ? await api.getUserReadings() : await api.getReadings();
      
      // Handle both old format (array) and new paginated format (object with readings array)
      let readings;
      if (Array.isArray(response)) {
        readings = response;
      } else if (response && response.readings && Array.isArray(response.readings)) {
        readings = response.readings;
      } else {
        console.error('Unexpected response format in exportDiary:', response);
        readings = [];
      }
      
      if (format === 'json') {
        const data = JSON.stringify(readings, null, 2);
        const blob = new Blob([data], { type: 'application/json' });
        return blob;
      }
      
      throw new Error('Only JSON export is currently supported');
    } catch (error) {
      console.error('Export diary error:', error);
      throw error;
    }
  }

  // Import diary data
  async importDiary(file) {
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      
      if (!Array.isArray(data)) {
        throw new Error('Invalid file format. Expected array of readings.');
      }

      let imported = 0;
      const errors = [];

      for (const reading of data) {
        try {
          await this.saveReading(reading);
          imported++;
        } catch (error) {
          errors.push({ reading: reading.id || 'unknown', error: error.message });
        }
      }

      return {
        success: true,
        imported,
        errors: errors.length > 0 ? errors : null,
        total: data.length
      };
    } catch (error) {
      console.error('Import diary error:', error);
      throw error;
    }
  }

  // Backup diary to local storage (fallback when offline)
  backupToLocalStorage(reading) {
    try {
      const existingBackups = JSON.parse(localStorage.getItem('diaryBackups') || '[]');
      const backup = {
        ...reading,
        backupDate: new Date().toISOString(),
        synced: false,
      };
      
      existingBackups.push(backup);
      localStorage.setItem('diaryBackups', JSON.stringify(existingBackups));
      
      return backup;
    } catch (error) {
      console.error('Local backup error:', error);
      throw error;
    }
  }

  // Sync local backups with server
  async syncBackups() {
    try {
      if (!authService.isAuthenticated()) {
        return { success: false, message: 'User not authenticated' };
      }

      const backups = JSON.parse(localStorage.getItem('diaryBackups') || '[]');
      const unsyncedBackups = backups.filter(backup => !backup.synced);

      if (unsyncedBackups.length === 0) {
        return { success: true, synced: 0 };
      }

      let syncedCount = 0;
      const errors = [];

      for (const backup of unsyncedBackups) {
        try {
          await this.saveReading(backup);
          backup.synced = true;
          syncedCount++;
        } catch (error) {
          errors.push({ backup: backup.id, error: error.message });
        }
      }

      // Update local storage with synced status
      localStorage.setItem('diaryBackups', JSON.stringify(backups));

      return {
        success: true,
        synced: syncedCount,
        errors: errors.length > 0 ? errors : null,
      };
    } catch (error) {
      console.error('Sync backups error:', error);
      throw error;
    }
  }

  // Get unsynced backups count
  getUnsyncedBackupsCount() {
    try {
      const backups = JSON.parse(localStorage.getItem('diaryBackups') || '[]');
      return backups.filter(backup => !backup.synced).length;
    } catch (error) {
      return 0;
    }
  }
}

// Create singleton instance
const diaryService = new DiaryService();
export default diaryService;
